import React, { useEffect, useState } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import axiosInstance from "../../Api/axios.instance";
import {
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { LoadingButton } from "@mui/lab";
import { grey } from "@mui/material/colors";
import useSession from "../../CustomHooks/useSession";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

export function getClusters() {
  return new Promise(async (resolve, reject) => {
    try {
      const { data, status } = await axiosInstance.get("/clusters/all");
      if (status === 200) resolve(data);
    } catch (error) {
      reject(error);
    }
  });
}

const emptyForm = {
  // CityId: "",
  RegionName: "",
  RegionNameUrdu: "",
  CreatedDate: dayjs().format("YYYY-MM-DD"),
  ClosedDate: "",
  Status: "Active",
  Reason: "",
  RegionOrder: "",
};

const STATUS_COLORS = {
  Active: "#1976d2",
  Inactive: "#f57c00",
  Closed: "#d32f2f",
};

const STATUS_LABELS = {
  Active: "Active",
  Inactive: "InActive",
  Closed: "Closed",
};

export default function Clusters() {
  const { currentPage, role } = useSession();
  const [currentPageCRUD] = React.useState(
    currentPage[0] ? currentPage[0] : "",
  );
  const roleObj = role ? JSON.parse(role) : null;
  const roleName = roleObj?.RoleName;
  const isAdminActive = roleName === "Admin Role - Active";
  const [clusters, setClusters] = React.useState([]);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const openAnchor = Boolean(anchorEl);

  const [openForm, setOpenForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    _fetchClusters();
  }, []);

  const _fetchClusters = async () => {
    try {
      const response = await getClusters();
      setClusters(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const _onDetailsClick = (details) => () => {
    navigate(`details/${details.RegionId}`, { state: { clusters: details } });
    handleAnchorClose();
  };

  const handleAnchorClose = () => setAnchorEl(null);
  const handleIconClick = (row) => (e) => {
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const handleAddOpen = () => {
    setIsEditing(false);
    setFormData(emptyForm);
    setOpenForm(true);
  };

  const handleEditOpen = () => {
    setIsEditing(true);
    setFormData({
      ...selectedRow,
      CreatedDate: selectedRow.CreatedDate
        ? dayjs(selectedRow.CreatedDate).format("YYYY-MM-DD")
        : "",
      ClosedDate: selectedRow.ClosedDate
        ? dayjs(selectedRow.ClosedDate).format("YYYY-MM-DD")
        : "",
    });
    setOpenForm(true);
    handleAnchorClose();
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setFormData(emptyForm);
  };

  const handleClear = () => setFormData(emptyForm);

  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCreatedDateChange = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      CreatedDate:
        newValue && dayjs(newValue).isValid()
          ? dayjs(newValue).format("YYYY-MM-DD")
          : "",
    }));
  };

  const handleClosedDateChange = (newValue) => {
    const formatted =
      newValue && dayjs(newValue).isValid()
        ? dayjs(newValue).format("YYYY-MM-DD")
        : "";
    setFormData((prev) => ({
      ...prev,
      ClosedDate: formatted,
      Status: formatted
        ? "Closed"
        : prev.Status === "Closed"
          ? "Active"
          : prev.Status,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const UserId = JSON.parse(localStorage.User)?.username;
      const payload = { ...formData, DataEntryOperator: UserId };

      if (isEditing) {
        await axiosInstance.put(`/clusters/${formData.RegionId}`, payload);
      } else {
        delete payload.ClosedDate;
        await axiosInstance.post("/clusters", payload);
      }
      await _fetchClusters();
      handleFormClose();
    } catch (error) {
      console.log(error?.response?.data?.message || error);
    } finally {
      setSaving(false);
    }
  };

  // const handleDelete = async () => {
  //   try {
  //     await axiosInstance.delete(`/clusters/${selectedRow.RegionId}`);
  //     await _fetchClusters();
  //   } catch (error) {
  //     console.log(error?.response?.data?.message || error);
  //   }
  //   handleAnchorClose();
  // };
  const handleDeleteClick = () => {
    setConfirmOpen(true);
    handleAnchorClose();
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`/clusters/${selectedRow.RegionId}`);
      await _fetchClusters();
    } catch (error) {
      console.log(error?.response?.data?.message || error);
    }
    setConfirmOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          my: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Clusters
          </Typography>
          <Typography variant="subtitle2" sx={{ color: grey[600] }}>
            List of Clusters (Regions)
          </Typography>
        </Box>
        {isAdminActive && (
          <Button variant="contained" onClick={handleAddOpen}>
            Add New Cluster
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="left">Cluster Name</TableCell>
              <TableCell align="center">Created Date</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Reason</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentPageCRUD.read &&
              clusters.map((row, index) => (
                <TableRow
                  key={String(index)}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {row.RegionName}
                  </TableCell>
                  <TableCell align="center">{row.CreatedDate}</TableCell>
                  <TableCell align="center">
                    <span
                      style={{
                        color: STATUS_COLORS[row.Status] || grey[600],
                        fontWeight: 600,
                      }}
                    >
                      {STATUS_LABELS[row.Status] || row.Status}
                    </span>
                  </TableCell>
                  <TableCell align="center">
                    {row.Reason === "" || row.Reason === null ? (
                      <Typography variant="subtitle2" sx={{ color: grey[600] }}>
                        No Reason Defined
                      </Typography>
                    ) : (
                      row.Reason
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={handleIconClick(row)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu anchorEl={anchorEl} open={openAnchor} onClose={handleAnchorClose}>
        <MenuItem onClick={_onDetailsClick(selectedRow)}>View Details</MenuItem>
        {isAdminActive && <MenuItem onClick={handleEditOpen}>Edit</MenuItem>}
        {/* <MenuItem onClick={handleDeleteClick} sx={{ color: "error.main" }}>
          Delete
        </MenuItem> */}
      </Menu>

      <Dialog fullWidth maxWidth="sm" open={openForm} onClose={handleFormClose}>
        <DialogTitle>
          {isEditing ? "Edit Cluster" : "Add New Cluster"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cluster Name"
                fullWidth
                size="small"
                name="RegionName"
                value={formData.RegionName || ""}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cluster Name Urdu"
                fullWidth
                size="small"
                name="RegionNameUrdu"
                value={formData.RegionNameUrdu || ""}
                onChange={handleChange}
              />
            </Grid>
            {/* <Grid item xs={12} md={6}>
              <TextField
                label="City Id"
                fullWidth
                size="small"
                type="number"
                name="CityId"
                value={formData.CityId || ""}
                onChange={handleChange}
              />
            </Grid> */}
            <Grid item xs={12} md={6}>
              <TextField
                select
                label="Status"
                fullWidth
                size="small"
                name="Status"
                value={formData.Status || "Active"}
                onChange={handleChange}
                disabled={!isEditing}
                SelectProps={{
                  native: false,
                }}
              >
                <MenuItem value="Active">
                  <span>Active</span>
                </MenuItem>
                {isEditing && (
                  <MenuItem value="Inactive">
                    <span>InActive</span>
                  </MenuItem>
                )}
                {isEditing && (
                  <MenuItem value="Closed">
                    <span>Closed</span>
                  </MenuItem>
                )}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <MobileDatePicker
                  label="Created On"
                  inputFormat="DD-MM-YYYY"
                  value={
                    formData.CreatedDate ? dayjs(formData.CreatedDate) : null
                  }
                  onChange={handleCreatedDateChange}
                  disabled={isEditing}
                  renderInput={(params) => (
                    <TextField fullWidth size="small" {...params} />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            {isEditing && (
              <Grid item xs={12} md={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MobileDatePicker
                    label="Closed On"
                    inputFormat="DD-MM-YYYY"
                    value={
                      formData.ClosedDate ? dayjs(formData.ClosedDate) : null
                    }
                    onChange={handleClosedDateChange}
                    renderInput={(params) => (
                      <TextField fullWidth size="small" {...params} />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <TextField
                label="Region Order"
                fullWidth
                size="small"
                type="number"
                name="RegionOrder"
                value={formData.RegionOrder || ""}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Reason"
                fullWidth
                size="small"
                name="Reason"
                value={formData.Reason || ""}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear}>Clear</Button>
          <LoadingButton
            loading={saving}
            onClick={handleSave}
            variant="contained"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Delete Cluster</DialogTitle>
        <DialogContent>
          Are you sure you want to delete "{selectedRow?.RegionName}"?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
